import type { ExpenseLineItem } from './expenses';

export interface RepairEstimatePricingSnapshot {
  id: string;
  label?: string;
  date: Date | string | number;
  amount: number;
  repairPartsAmount: number | null;
  repairLaborAmount: number | null;
  repairFeesAmount: number | null;
  repairTaxAmount: number | null;
  lineItems: ExpenseLineItem[] | null;
  sourceRepairEstimateStoragePath?: string | null;
  attachmentURL?: string | null;
  reviewStatus?: string | null;
}

export interface RepairEstimateCompareMetricRow {
  label: string;
  olderValue: number | null;
  newerValue: number | null;
  delta: number | null;
}

export interface RepairEstimateCompareLineRow {
  key: string;
  description: string;
  olderAmount: number | null;
  newerAmount: number | null;
  delta: number | null;
}

export interface RepairEstimateComparison {
  older: RepairEstimatePricingSnapshot;
  newer: RepairEstimatePricingSnapshot;
  metrics: RepairEstimateCompareMetricRow[];
  lineItems: RepairEstimateCompareLineRow[];
}

const toNumber = (value: number | null | undefined): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const toTimestamp = (value: Date | string | number | null | undefined): number => {
  if (value == null) return 0;
  const parsed = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const lineItemKey = (item: ExpenseLineItem): string => {
  const description = (item.description || '').trim().toLowerCase();
  const subcategory = (item.subcategory || '').trim().toLowerCase();
  return subcategory ? `${subcategory}::${description}` : description || 'unknown';
};

export const sortRepairEstimateSnapshots = <
  T extends { date: Date | string | number; created_at?: Date | string | number | null },
>(
  items: T[],
): T[] =>
  [...items].sort(
    (left, right) =>
      toTimestamp(left.created_at ?? left.date) - toTimestamp(right.created_at ?? right.date),
  );

export const buildRepairEstimateComparison = (
  older: RepairEstimatePricingSnapshot,
  newer: RepairEstimatePricingSnapshot,
): RepairEstimateComparison => {
  const metrics: RepairEstimateCompareMetricRow[] = [
    { label: 'Total', olderValue: toNumber(older.amount), newerValue: toNumber(newer.amount) },
    { label: 'Parts', olderValue: older.repairPartsAmount, newerValue: newer.repairPartsAmount },
    { label: 'Labor', olderValue: older.repairLaborAmount, newerValue: newer.repairLaborAmount },
    { label: 'Fees', olderValue: older.repairFeesAmount, newerValue: newer.repairFeesAmount },
    { label: 'Tax', olderValue: older.repairTaxAmount, newerValue: newer.repairTaxAmount },
  ].map(row => ({
    ...row,
    delta:
      row.olderValue != null && row.newerValue != null ? row.newerValue - row.olderValue : null,
  }));

  const olderLines = new Map<string, { description: string; amount: number }>();
  const newerLines = new Map<string, { description: string; amount: number }>();

  for (const item of older.lineItems ?? []) {
    const key = lineItemKey(item);
    const amount = toNumber(item.amount) ?? 0;
    const existing = olderLines.get(key);
    olderLines.set(key, {
      description: item.description || existing?.description || key,
      amount: (existing?.amount ?? 0) + amount,
    });
  }

  for (const item of newer.lineItems ?? []) {
    const key = lineItemKey(item);
    const amount = toNumber(item.amount) ?? 0;
    const existing = newerLines.get(key);
    newerLines.set(key, {
      description: item.description || existing?.description || key,
      amount: (existing?.amount ?? 0) + amount,
    });
  }

  const keys = Array.from(new Set([...Array.from(olderLines.keys()), ...Array.from(newerLines.keys())]));
  const lineItems: RepairEstimateCompareLineRow[] = keys.map(key => {
    const olderEntry = olderLines.get(key);
    const newerEntry = newerLines.get(key);
    const olderAmount = olderEntry ? olderEntry.amount : null;
    const newerAmount = newerEntry ? newerEntry.amount : null;
    return {
      key,
      description: newerEntry?.description || olderEntry?.description || key.replace('::', ' — '),
      olderAmount,
      newerAmount,
      delta: olderAmount != null && newerAmount != null ? newerAmount - olderAmount : null,
    };
  });

  lineItems.sort((left, right) => left.description.localeCompare(right.description));

  return { older, newer, metrics, lineItems };
};
